import React, { CSSProperties, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { HiDownload, HiOutlineMail } from "react-icons/hi";
import { everyTrue, hasTrue, isValidObject } from "@/helper/helper";
import { useRegisterRequired, useSignUpModal } from "@/states/ui.state";
import { useConfig, useError } from "@/states/common.state";
import { useSolutionsSelect } from "@/states/solution.state";
import Popover from "@/components/common/DefaultPopover";
import { FcGoogle } from "react-icons/fc";
import { useInView } from "react-intersection-observer";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import { typesRequiredRegister } from "@/components/page-modules/home-page/GenerateTypes/PreviewResult/data/preview-result.repository";
import { OverlayTriggerHandle, OverlayTriggerProps } from "rsuite/esm/internals/Overlay/OverlayTrigger";
import { useTranslations } from "next-intl";
import { useDownloadClickedState } from "@/states/user.state";
import { AzureButton } from "@/components/common/AzureButton";
import { VARIANT } from "@/constant";
import { useMediaQuery } from "react-responsive";
import classes from "./Download.module.scss";
import IFThen from "@/components/common/IFThen";
import { useQrShowState } from "@/states/qr.state";
import { sendGTMEvent } from "@next/third-parties/google";
import { gtm } from "@/gtm/events";
import { useGtmState } from "@/gtm/gtm.state";
import useGoogleSignIn from "@/hooks/user/register/useGoogleSignIn";
import useMicrosoftSignIn from "@/hooks/user/register/useMicrosoftSignIn";
const Download = ({
  handlePopupInPreview,
  TEST_VARIANT = VARIANT.VariantB,
  title,
  setActiveTab
}: {
  handlePopupInPreview?: () => void;
  TEST_VARIANT: VARIANT;
  title?: string;
  setActiveTab?: (val: number) => void;
}) => {
  const {
    solutionsSelectedTypes
  } = useSolutionsSelect();
  const [placement, setPlacement] = useState<OverlayTriggerProps["placement"]>("top");
  const [downloadButtonWidth, setDownloadButtonWidth] = useState(365);
  const {
    dashboardQr
  } = useQrShowState();
  const {
    setDownloadClicked
  } = useDownloadClickedState();
  const isMobile = useMediaQuery({
    maxWidth: 500
  });
  const isTablet = useMediaQuery({
    maxWidth: 1024
  });
  const {
    ref,
    inView
  } = useInView();
  const popoverRef = useRef<OverlayTriggerHandle>(null);
  const signupModalState = useSignUpModal();
  const {
    isTypeRequireRegister
  } = useRegisterRequired();
  const {
    error: validationErrors
  } = useError();
  const {
    payload,
    config,
    files,
    isLoading
  } = useConfig();
  const {
    mutateHomeQr
  } = useGenerateHomeQr();
  const t = useTranslations("Home");
  const gtmState = useGtmState();
  const {
    onSignIn: onGoogleSignIn
  } = useGoogleSignIn(() => gtmState.onChange(gtm.homePage.dropdown_signup_by_google, true));
  const {
    onSignIn: onMicrosoftSignIn
  } = useMicrosoftSignIn(() => gtmState.onChange(gtm.homePage.dropdown_signup_by_azure, true));
  useEffect(() => {
    sendGTMEvent({
      event: gtm.homePage.download_btn_visible
    });
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  useEffect(() => {
    signupModalState.setIsTriedDownload(false);
  }, [payload]);
  useEffect(() => {
    handleScroll(); // for inView render
    if (!inView) {
      popoverRef.current?.close();
    }
    ;
  }, [inView]);
  useEffect(() => {
    if (popoverRef.current) popoverRef.current.close();
  }, [solutionsSelectedTypes]);
  function onClosePopover() {
    if (popoverRef.current) {
      popoverRef.current?.close();
    }
  }
  function onOpenPopover() {
    if (popoverRef.current) {
      popoverRef.current?.open();
    }
  }
  function onEmail() {
    onClosePopover();
    setDownloadClicked(false);
    if (TEST_VARIANT === VARIANT.VariantE && handlePopupInPreview && !isMobile) {
      return handlePopupInPreview();
    }
    signupModalState.setIsSignUpModal(true);
  }
  function isOnlyRedirectType(payload: Record<string, any>): boolean {
    if (!payload || typeof payload !== "object") {
      return false;
    }
    const keys = Object.keys(payload);
    return keys.length === 1 && keys[0] === "redirect_type";
  }
  async function onDownload(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    sendGTMEvent({
      event: gtm.homePage.download_btn_clicked
    });
    const requiredSettingTab = hasTrue(isValidObject(validationErrors), !isValidObject({
      ...payload,
      ...files
    }), isOnlyRedirectType({
      ...payload,
      ...files
    }), dashboardQr?.hasFileError);
    if (requiredSettingTab && isTablet && !isLoading) {
      setActiveTab?.(1);
      mutateHomeQr({
        params: {
          ...payload,
          config,
          files
        }
      });
    }
    if (typesRequiredRegister.includes(solutionsSelectedTypes!)) {
      return;
    }
    handleModalState();
    handleDownloadProcess(requiredSettingTab);
  }
  function handleModalState() {
    onClosePopover();
    signupModalState.setIsTriedDownload(true);
    if (!isTablet) {
      signupModalState.setIsTempTrigger();
    }
  }
  function handleDownloadProcess(hasError: boolean) {
    if (!hasError) {
      onOpenPopover();
      setDownloadClicked(true);
      sendGTMEvent({
        event: gtm.homePage.download_dropdown_visible
      });
    } else if (!isTablet && !isLoading) {
      mutateHomeQr({
        params: {
          ...payload,
          config,
          files
        }
      });
    }
  }
  const handleScroll = () => {
    const currentPosition = popoverRef.current?.root?.getBoundingClientRect();
    if (popoverRef.current) {
      setDownloadButtonWidth(popoverRef.current?.root?.clientWidth || 365);
    }
    if (currentPosition) {
      const isBottomPlacement = window.innerHeight - currentPosition.top >= 260;
      const newPlacement = isBottomPlacement ? "bottom" : "top";
      setPlacement(newPlacement);
    }
  };
  const googleSigninHandler = () => {
    setDownloadClicked(false);
    onGoogleSignIn();
  };
  return <div data-sentry-component="Download" data-sentry-source-file="Download.tsx">
      <div className={classes.PreviewAltCover} ref={ref}>
        <Popover ref={popoverRef} placement={placement} trigger={typesRequiredRegister.includes(solutionsSelectedTypes!) ? "click" : "none"} whisper={<button data-testid="downloadBtn" id="btn_qr_download_btn" className={cn(classes.DownloadBtn, {
        [classes.disabled]: everyTrue(isTypeRequireRegister, !isTablet)
      })} onClick={onDownload} disabled={everyTrue(isTypeRequireRegister, !isTablet)}>
              <IFThen condition={!!title} Then={<span>{title}</span>} Else={<IFThen condition={everyTrue(isTablet, typesRequiredRegister.includes(solutionsSelectedTypes ?? ""))} Then={<span>{t("qrTypeSignUpBtnMobile")}</span>} Else={<>
                        <span><HiDownload color={"#ffffff"} height={18} width={14} /></span>
                        <span>{t("downloadBtn")}</span>
                      </>} />} />
            </button>} data-sentry-element="Popover" data-sentry-source-file="Download.tsx">
          <div id="popover_wrapper" className={classes.PopoverDownload} style={{
          "--width": `${downloadButtonWidth}px`,
          "--direction": `${placement === "bottom" ? "column" : "column-reverse"}`
        } as CSSProperties}>
            <ul>
              <li onClick={() => sendGTMEvent({
              event: gtm.homePage.dropdown_signup_by_google_btn
            })}>
                <a id="btn_continue_with_google" onClick={googleSigninHandler} rel="noopener noreferrer">
                  <FcGoogle size={20} data-sentry-element="FcGoogle" data-sentry-source-file="Download.tsx" />
                  {isTypeRequireRegister ? t("ContinueWithGoogle") : t("DownloadWithGoogle")}
                </a>
              </li>

              <li onClick={() => sendGTMEvent({
              event: gtm.homePage.dropdown_signup_by_azure_btn
            })}>
                <AzureButton variant="ghost" onClick={e => {
                setDownloadClicked(false);
                onMicrosoftSignIn(e);
              }} text={isTypeRequireRegister ? t("ContinueWithMicrosoft") : t("DownloadWithMicrosoft")} data-sentry-element="AzureButton" data-sentry-source-file="Download.tsx" />
              </li>
              <li onClick={() => sendGTMEvent({
              event: gtm.homePage.dropdown_signup_by_email_btn
            })} className={classes.WithEmail} style={{
              "--top": `${placement === "bottom" ? "0" : "100%"}`,
              "--bottom": `${placement === "bottom" ? "-100%" : "200%"}`
            } as CSSProperties}>
                <span id="btn_continue_with_email" onClick={onEmail}>
                  <HiOutlineMail size={22} color="#A1A1A1" data-sentry-element="HiOutlineMail" data-sentry-source-file="Download.tsx" /> {isTypeRequireRegister ? t("ContinueWithEmail") : t("DownloadWithEmail")}
                </span>
              </li>
            </ul>
          </div>
        </Popover>
      </div>
    </div>;
};
export default Download;